/**
 * Super Admin "view as" (docs/specs/staff-view-as.md), customer + partner portal side.
 * The admin console mints a one-time link `https://<portal|partners>/view-as#code=...` (60 s, single use). The
 * `/view-as` page redeems it (creating a staff-owned session on this host), and the shell shows an always-visible
 * banner while that session lasts. The server is authoritative; these helpers never throw.
 */
import { bff, bffJson } from './bff';

/** The one-time code from the fragment (`#code=`); the fragment never reaches a server log. */
export function viewAsCodeFromHash(hash: string): string | null {
  const m = /(?:^#|&)code=([A-Za-z0-9_-]{16,200})(?:&|$)/.exec(hash);
  return m?.[1] ?? null;
}

export interface ViewAsStatus { active: boolean; reason: string | null; expiresAt: string | null }
export const VIEW_AS_INACTIVE: ViewAsStatus = { active: false, reason: null, expiresAt: null };

/** Redeem the code. Any failure (invalid, expired, used, wrong host, network) is one uniform outcome. */
export async function redeemViewAs(
  code: string | null,
  post: (body: { code: string }) => Promise<unknown> = (body) => bffJson('/v1/identity/view-as/consume', 'POST', body),
): Promise<'ok' | 'invalid'> {
  if (!code) return 'invalid';
  try {
    await post({ code });
    return 'ok';
  } catch {
    return 'invalid';
  }
}

/** Banner data. Errors and anything unexpected read as "not viewing as". */
export async function loadViewAsStatus(
  get: () => Promise<Partial<ViewAsStatus> | null> = () => bff<Partial<ViewAsStatus> | null>('/v1/identity/view-as/status'),
): Promise<ViewAsStatus> {
  try {
    const s = await get();
    if (!s || s.active !== true) return VIEW_AS_INACTIVE;
    const reason = typeof s.reason === 'string' && s.reason.trim() ? s.reason.trim() : null;
    return { active: true, reason, expiresAt: typeof s.expiresAt === 'string' ? s.expiresAt : null };
  } catch {
    return VIEW_AS_INACTIVE;
  }
}

export interface ClosableWindow { close(): void; readonly closed: boolean; location: { assign(url: string): void } }

/** End the view-as session, then close the tab the admin console opened; if the browser keeps it open, go to sign-in. */
export async function endViewAs(
  win: ClosableWindow = window,
  post: () => Promise<unknown> = () => bffJson('/v1/identity/view-as/end', 'POST'),
): Promise<void> {
  await post().catch(() => undefined);
  try { win.close(); } catch { /* not closable */ }
  if (!win.closed) win.location.assign('/signin');
}
